import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { AccessibilityModeEnum, TextSizeEnum, ThemeEnum } from '../../utils/enums';
import { AccessibilityContextType, AccessibilitySettings } from './AccessibilityContext.types';

const AccessibilityContext = createContext<AccessibilityContextType | undefined>(undefined);

const DEFAULT_SETTINGS: AccessibilitySettings = {
  mode: AccessibilityModeEnum.Regular,
  theme: ThemeEnum.Basic,
  textSize: TextSizeEnum.Medium,
};

interface AccessibilityProviderProps {
  children: ReactNode;
}

export const AccessibilityProvider: React.FC<AccessibilityProviderProps> = ({ children }) => {
  const [settings, setSettings] = useState<AccessibilitySettings>(DEFAULT_SETTINGS);

  const setMode = useCallback((mode: AccessibilityModeEnum) => {
    setSettings((prev) => ({ ...prev, mode }));
  }, []);

  const setTheme = useCallback((theme: ThemeEnum) => {
    setSettings((prev) => ({ ...prev, theme }));
  }, []);

  const setTextSize = useCallback((textSize: TextSizeEnum) => {
    setSettings((prev) => ({ ...prev, textSize }));
  }, []);

  const updateSettings = useCallback((newSettings: Partial<AccessibilitySettings>) => {
    setSettings((prev) => ({ ...prev, ...newSettings }));
  }, []);

  const resetSettings = useCallback(() => {
    setSettings(DEFAULT_SETTINGS);
  }, []);

  const contextValue: AccessibilityContextType = useMemo(
    () => ({
      settings,
      setMode,
      setTheme,
      setTextSize,
      updateSettings,
      resetSettings,
    }),
    [settings]
  );

  useEffect(() => {
    const body = document.body;

    body.classList.remove(
      'accessibility-mode-regular',
      'accessibility-mode-enhanced',
      'accessibility-theme-light',
      'accessibility-theme-dark',
      'accessibility-text-small',
      'accessibility-text-medium',
      'accessibility-text-large'
    );

    body.classList.add(`accessibility-mode-${settings.mode}`);
    body.classList.add(`accessibility-theme-${settings.theme}`);
    body.classList.add(`accessibility-text-${settings.textSize}`);

    return () => {
      body.classList.remove(
        'accessibility-mode-regular',
        'accessibility-mode-enhanced',
        'accessibility-theme-light',
        'accessibility-theme-dark',
        'accessibility-text-small',
        'accessibility-text-medium',
        'accessibility-text-large'
      );
    };
  }, [settings.mode, settings.theme, settings.textSize]);

  return (
    <AccessibilityContext.Provider value={contextValue}>{children}</AccessibilityContext.Provider>
  );
};

export const useAccessibility = (): AccessibilityContextType => {
  const context = useContext(AccessibilityContext);
  if (context === undefined) {
    throw new Error('useAccessibility must be used within an AccessibilityProvider');
  }
  return context;
};
